import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, FileText, ExternalLink } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EnquiryForm from "@/components/EnquiryForm";
import { FadeIn, MaskedLineInView } from "@/components/Reveal";
import { CONTACT, SERVICES_HEADER_IMAGE } from "@/data/site";

export default function Contact() {
    const [searchParams] = useSearchParams();
    const preselected = searchParams.get("service") || "";

    return (
        <main data-testid="contact-page">
            <PageHeader
                eyebrow="Contact Us"
                title={
                    <>
                        Let's talk about
                        <br />
                        your <span className="text-accent">project</span>
                    </>
                }
                lead="Call, email or send an enquiry — our team responds with practical, engineering-first advice."
                image={SERVICES_HEADER_IMAGE}
            />

            <section className="py-20 lg:py-28">
                <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-12">
                    <div className="space-y-5 lg:col-span-5">
                        <FadeIn>
                            <div
                                data-testid="contact-phones-card"
                                className="border border-line bg-card p-7 transition-colors duration-300 hover:border-accent/50"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                                        <Phone className="h-4 w-4" />
                                    </span>
                                    <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400">
                                        Phone
                                    </h2>
                                </div>
                                <div className="mt-5 space-y-2">
                                    {CONTACT.phones.map((p, i) => (
                                        <a
                                            key={p.href}
                                            href={p.href}
                                            data-testid={`contact-phone-${i + 1}`}
                                            className="block font-display text-2xl font-semibold tracking-wide text-white transition-colors hover:text-accent sm:text-3xl"
                                        >
                                            {p.display}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.06}>
                            <div
                                data-testid="contact-emails-card"
                                className="border border-line bg-card p-7 transition-colors duration-300 hover:border-accent/50"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                                        <Mail className="h-4 w-4" />
                                    </span>
                                    <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400">
                                        Email
                                    </h2>
                                </div>
                                <div className="mt-5 space-y-2">
                                    {CONTACT.emails.map((e, i) => (
                                        <a
                                            key={e.href}
                                            href={e.href}
                                            data-testid={`contact-email-${i + 1}`}
                                            className="block break-all text-sm text-white transition-colors hover:text-accent sm:text-base"
                                        >
                                            {e.display}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.12}>
                            <div
                                data-testid="contact-address-card"
                                className="border border-line bg-card p-7 transition-colors duration-300 hover:border-accent/50"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center border border-accent/40 bg-night/60 text-accent">
                                        <MapPin className="h-4 w-4" />
                                    </span>
                                    <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-slate-400">
                                        Address
                                    </h2>
                                </div>
                                <address
                                    data-testid="contact-address"
                                    className="mt-5 text-sm not-italic leading-relaxed text-slate-300"
                                >
                                    {CONTACT.addressLines.map((line) => (
                                        <span key={line} className="block">
                                            {line}
                                        </span>
                                    ))}
                                </address>
                                <a
                                    href={CONTACT.mapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-maps-directions-link"
                                    className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-accent hover:underline"
                                >
                                    Get Directions
                                    <ExternalLink className="h-3 w-3" />
                                </a>
                            </div>
                        </FadeIn>

                        <FadeIn delay={0.18}>
                            <div
                                data-testid="contact-contract-card"
                                className="flex items-center justify-between gap-4 border border-dashed border-line bg-night/40 px-7 py-5"
                            >
                                <span className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-slate-400">
                                    <FileText className="h-4 w-4 text-slate-500" />
                                    Contract No.
                                </span>
                                <span className="font-mono text-xs text-slate-500">
                                    [To be filled by company]
                                </span>
                            </div>
                        </FadeIn>
                    </div>

                    <FadeIn delay={0.1} className="lg:col-span-7">
                        <EnquiryForm preselected={preselected} />
                    </FadeIn>
                </div>
            </section>

            <section className="border-t border-line pb-20 lg:pb-28" data-testid="contact-map-section">
                <div className="mx-auto w-full max-w-7xl px-4 pt-16 sm:px-6 lg:px-12">
                    <MaskedLineInView>
                        <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent">
                            <span className="h-px w-10 bg-accent" aria-hidden="true" />
                            Find us
                        </p>
                    </MaskedLineInView>
                    <MaskedLineInView delay={0.1}>
                        <h2 className="mt-4 font-display text-4xl font-bold uppercase text-white sm:text-5xl">
                            Hebbagodi, Bangalore
                        </h2>
                    </MaskedLineInView>
                    <FadeIn delay={0.15}>
                        <div
                            data-testid="contact-map"
                            className="mt-10 overflow-hidden border border-line"
                        >
                            <iframe
                                title="UMA ENGINEERING location — Hebbagodi, Bangalore"
                                src={CONTACT.mapsEmbed}
                                loading="lazy"
                                className="aspect-[16/9] w-full grayscale-[0.4] invert-[0.92] hue-rotate-180 contrast-[0.9] sm:aspect-[21/9]"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
}
